import React, { useState, useEffect, useCallback, useRef } from "react";
import { toast } from "react-toastify";
import { useNavigate, useLocation } from "react-router-dom";
import { BASE_URL, API_ENDPOINTS } from "../../config/apiconfig";
import api from "../../api/axiosInstance";
import ConfirmModal from "../../components/ConfirmModal";
import PassbookDownloadModal from "../../components/PassbookDownloadModal";
import PdfPreviewModal from "../../components/PdfPreviewModal";
import TemplatePreviewModal from "../../components/TemplatePreviewModal";
import AppDropdown from "../../components/AppDropdown";
import { useColorMode } from "../../contexts/ColorModeContext";
import "../../style/PassbookDownloadModal.css";
import "../../style/CustomerDetails.css";
import "../../styles/theme.css";
import "../../styles/GlobalForm.css";
import "../../styles/GlobalModal.css";
import {
  Box,
  Typography,
  Card,
  CardContent,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Pagination,
  Skeleton,
  Dialog,
  DialogContent,
  DialogTitle,
  DialogActions,
  Divider,
  CircularProgress,
  useMediaQuery,
  useTheme,
  Tooltip,
} from "@mui/material";
import {
  Search as SearchIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  Clear as ClearIcon,
  Person as PersonIcon,
  Book as BookIcon,
  Description as DescriptionIcon,
  Inventory as InventoryIcon,
  Close as CloseIcon,
  ArrowBack as ArrowBackIcon,
  FileDownload as DownloadIcon,
  FileUpload as UploadIcon,
} from "@mui/icons-material";

const AnimatedRefreshIcon = ({ size = 18 }) => (
  <svg 
    xmlns="http://www.w3.org/2000/svg" 
    width={size} 
    height={size} 
    viewBox="0 0 24 24" 
    fill="none" 
    stroke="currentColor" 
    strokeWidth="2" 
    strokeLinecap="round" 
    strokeLinejoin="round"
  >
    <g>
      <path d="M21 12a9 9 0 1 1-2.64-6.36" />
      <polyline points="21 3 21 9 15 9" />
      <animateTransform
        attributeName="transform"
        attributeType="XML"
        type="rotate"
        from="0 12 12"
        to="360 12 12"
        dur="1s"
        repeatCount="1"
        fill="freeze" />
    </g>
  </svg>
);

function CustomerDetailsPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { mode } = useColorMode();
  const isDark = mode === 'dark';
  const [customers, setCustomers] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterOpen, setFilterOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [confirmModal, setConfirmModal] = useState({ isOpen: false, customerId: null });
  const [passbookModal, setPassbookModal] = useState({ isOpen: false, customerId: null, customerName: null });
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [passbookDialogOpen, setPassbookDialogOpen] = useState(false);
  const [statementDialogOpen, setStatementDialogOpen] = useState(false);
  const [productDialogOpen, setProductDialogOpen] = useState(false);
  const [pdfModalOpen, setPdfModalOpen] = useState(false);
  const [popupMessage, setPopupMessage] = useState({ open: false, title: '', message: '', type: 'info', errorReport: null });
  const [pdfUrl, setPdfUrl] = useState(null);
  const [pdfType, setPdfType] = useState(''); // 'statement' or 'product'
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [products, setProducts] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState('');
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [currentDialog, setCurrentDialog] = useState('main'); // 'main', 'statement', 'product'
  const [downloadingProductPdf, setDownloadingProductPdf] = useState(null);
  // eslint-disable-next-line no-unused-vars
  const [templateModalOpen, setTemplateModalOpen] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [templateHeaders, setTemplateHeaders] = useState({ customerHeaders: [], productHeaders: [] });
  // eslint-disable-next-line no-unused-vars
  const [loadingTemplate, setLoadingTemplate] = useState(false);
  const fetchRequestIdRef = useRef(0);

  // === FETCH CUSTOMER ===
  const fetchCustomers = useCallback(async () => {
    const requestId = ++fetchRequestIdRef.current;
    setLoading(true);

    try {
      // Detect if search term is an email
      const isEmail = searchTerm.includes('@');
      const nameParam = isEmail ? '' : searchTerm;
      const emailParam = isEmail ? searchTerm : '';

      const response = await fetch(
        `${BASE_URL}${API_ENDPOINTS.CUSTOMERS.SEARCH}?page=${currentPage}&size=${rowsPerPage}&name=${nameParam}&email=${emailParam}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      if (!response.ok) throw new Error();

      const data = await response.json();

      // Ignore this response if a newer request has since been fired
      if (requestId !== fetchRequestIdRef.current) return;

      const sortedCustomers = (data.content || []).sort((a, b) => 
        a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })
      );
      setCustomers(sortedCustomers);
      setTotalPages(data.totalPages || 0);

    } catch {
      if (requestId === fetchRequestIdRef.current) {
        toast.error("Error fetching customers");
        setCustomers([]);
        setTotalPages(0);
      }
    } finally {
      if (requestId === fetchRequestIdRef.current) {
        setLoading(false);
      }
    }
  }, [currentPage, searchTerm, rowsPerPage]);

  useEffect(() => {
    const quickSearch = location.state?.quickSearch;
    if (quickSearch) {
      const value = String(quickSearch).trim();
      if (value) {
        setSearchTerm(value);
        setCurrentPage(0);
        setFilterOpen(true);
      }
      // Clear location.state after applying to prevent re-triggering
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, location.pathname, navigate]);

  useEffect(() => {
    // Skip firing on the very first render if a quickSearch value is about to be applied
    if (location.state?.quickSearch && !searchTerm) return;
    fetchCustomers();
  }, [currentPage, searchTerm, rowsPerPage, fetchCustomers, location.state]);

  // === SEARCH ===
  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(0);
  };

  const clearAllFilters = () => {
    setSearchTerm("");
    setCurrentPage(0);
  };

  const handleRefresh = async () => {
    setIsRefreshing(true);
    await fetchCustomers();
    setTimeout(() => setIsRefreshing(false), 500);
  };

  const handleExport = async () => {
    try {
      const response = await api.get("customers/export", {
        responseType: "blob",
        headers: { Accept: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" },
      });

      const blob = response.data;
      if (!(blob instanceof Blob) || blob.size === 0) {
        throw new Error("The server returned an empty export file");
      }
      const fileUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = fileUrl;
      link.download = `customers-export-${new Date().toISOString().split("T")[0]}.xlsx`;
      link.style.display = "none";
      document.body.appendChild(link);
      link.click();
      setTimeout(() => {
        document.body.removeChild(link);
        window.URL.revokeObjectURL(fileUrl);
      }, 100);

      setPopupMessage({
        open: true,
        title: 'Export Successful',
        message: 'Customers exported successfully',
        type: 'success'
      });
    } catch (error) {
      console.error("Export error:", error);
      setPopupMessage({
        open: true,
        title: 'Export Error',
        message: `Failed to export customers: ${error.message || 'Unknown error'}`,
        type: 'error'
      });
    }
  };

const handleImport = () => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = ".xlsx,.xls";
    input.onchange = async (e) => {
      const file = e.target.files[0];
      if (!file) return;

      const formData = new FormData();
      formData.append("file", file);

      try {
        const token = localStorage.getItem("token");
        const response = await fetch(
          `${BASE_URL}/customers/import`,
          {
            method: "POST",
            headers: {
              Authorization: `Bearer ${token}`,
            },
            body: formData,
          }
        );

        if (!response.ok) {
          throw new Error("Import failed");
        }

        const result = await response.json();
       
      if (result.duplicates > 0) {
        setPopupMessage({
          open: true,
          title: 'Import Completed with Duplicates',
          message: `${result.duplicates} duplicates found, ${result.inserted} new records inserted out of ${result.total} total records`,
          type: 'warning',
          errorReport: result.errorReport
        });
      } else if (result.invalid > 0) {
        setPopupMessage({
          open: true,
          title: 'Import Failed',
          message: `${result.invalid} invalid records out of ${result.total} total records`,
          type: 'error',
          errorReport: result.errorReport
        });
      } else {
        setPopupMessage({
          open: true,
          title: 'Import Successful',
          message: `${result.inserted} records inserted out of ${result.total} total records`,
          type: 'success',
          errorReport: null
        });
      }  
         
        setTemplateModalOpen(false);
        fetchCustomers();
      } catch (error) {
        console.error("Import error:", error);
        setPopupMessage({
          open: true,
          title: 'Import Error',
          message: `Failed to import customers: ${error.message || 'Unknown error'}`,
          type: 'error'
        });
      }
    };
    input.click();
  };

  const handleDownloadErrorReport = () => {
    if (popupMessage.errorReport) {
      const byteCharacters = atob(popupMessage.errorReport);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `duplicate-error-report-${new Date().toISOString().split('T')[0]}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    }
  };

  // === TEMPLATE FUNCTIONS ===
  const handleDownloadTemplate = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(
        `${BASE_URL}/customers/template`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error("Template download failed");
      }

      const blob = await response.blob();
      const fileUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = fileUrl;
      link.download = `Customer_Template.xlsx`;
      link.style.display = "none";
      document.body.appendChild(link);
      link.click();
      setTimeout(() => {
        document.body.removeChild(link);
        window.URL.revokeObjectURL(fileUrl);
      }, 100);

      toast.success("Template downloaded successfully");
    } catch (error) {
      console.error("Template download error:", error);
      toast.error("Failed to download template");
    }
  };

  // eslint-disable-next-line no-unused-vars
  const handleViewTemplate = async () => {
    setTemplateModalOpen(true);
    setLoadingTemplate(true);
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(
        `${BASE_URL}/customers/template/headers`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (!response.ok) {
        throw new Error("Failed to fetch template headers");
      }

      const data = await response.json();
      setTemplateHeaders({
        customerHeaders: data.customerHeaders || [],
        customerRequiredHeaders: data.customerRequiredHeaders || [],
        customerOptionalHeaders: data.customerOptionalHeaders || [],
        productHeaders: data.productHeaders || [],
        productRequiredHeaders: data.productRequiredHeaders || [],
        productOptionalHeaders: data.productOptionalHeaders || [],
      });
    } catch (error) {
      console.error("Template headers fetch error:", error);
      toast.error("Failed to load template preview");
    } finally {
      setLoadingTemplate(false);
    }
  };

  // eslint-disable-next-line no-unused-vars
  const handleAnalyzeFile = async (file) => {
    if (!file) return;
    setLoadingTemplate(true);
    try {
      const token = localStorage.getItem("token");
      const formData = new FormData();
      formData.append("file", file);

      const response = await fetch(
        `${BASE_URL}/customers/template/extract-headers`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
          },
          body: formData,
        }
      );

      if (!response.ok) {
        throw new Error("Failed to analyze file");
      }

      const data = await response.json();
      // Show analysis in template modal or separate modal
      toast.success(`File analyzed: ${data.sheets ? Object.keys(data.sheets).join(', ') : 'No sheets'}`);
      // You could display data.analysis here
    } catch (error) {
      console.error("File analysis error:", error);
      toast.error("Failed to analyze file");
    } finally {
      setLoadingTemplate(false);
    }
  };


  // === VIEW PASSBOOK ===
  const viewPassbook = (customerId, customerName) => {
    navigate(`/dashboard/customers/passbook?customerId=${customerId}`);
  };

  const handlePassbookMenuClick = (event, customer) => {
    event.stopPropagation();
    setSelectedCustomer(customer);
    setPassbookDialogOpen(true);
  };

  const handlePassbookMenuClose = () => {
    setPassbookDialogOpen(false);
    setSelectedCustomer(null);
  };

  const handleViewPassbookClick = () => {
    if (selectedCustomer) {
      viewPassbook(selectedCustomer.id, selectedCustomer.name);
    }
    handlePassbookMenuClose();
  };

  const handleDownloadStatementClick = () => {
    setStatementDialogOpen(true);
    setFromDate('');
    setToDate('');
    setPdfType('statement');
    setCurrentDialog('statement');
    setPassbookDialogOpen(false);
  };

  const handleDownloadProductClick = async () => {
    if (!selectedCustomer) return;

    setLoadingProducts(true);
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(
        `${BASE_URL}${API_ENDPOINTS.PASSBOOK.GET_SUMMARY(selectedCustomer.id)}`,
        {
          method: "GET",
          headers: {
            "Authorization": `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error("Failed to fetch products");
      }

      const data = await response.json();
      console.log("Product data:", data); // Debug log

      // Check different possible response structures
      const productItems = data.items || data.products || data || [];
      setProducts(Array.isArray(productItems) ? productItems : []);
      setSelectedProduct('');
      setProductDialogOpen(true);
      setPdfType('product');
      setCurrentDialog('product');
      setPassbookDialogOpen(false);
    } catch (error) {
      console.error("Error fetching products:", error);
      toast.error("Failed to fetch products");
    } finally {
      setLoadingProducts(false);
    }
  };

  const handleStatementPreview = async () => {
    if (!selectedCustomer) {
      toast.error("Please select a customer");
      return;
    }

    setDownloadingPdf(true);
    try {
      let url = `${BASE_URL}${API_ENDPOINTS.PASSBOOK.GENERATE_STATEMENT_PDF}?customerId=${selectedCustomer.id}`;
      
      if (fromDate && toDate) {
        url += `&fromDate=${encodeURIComponent(fromDate)}&toDate=${encodeURIComponent(toDate)}`;
      }

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem("token")}`,
        },
      });

      if (!response.ok) {
        throw new Error("Failed to generate statement PDF");
      }

      const blob = await response.blob();
      const pdfUrl = window.URL.createObjectURL(blob);
      setPdfUrl(pdfUrl);
      setStatementDialogOpen(false);
      setPdfModalOpen(true);
    } catch (error) {
      console.error("Error generating statement PDF:", error);
      toast.error("Failed to generate statement PDF");
    } finally {
      setDownloadingPdf(false);
    }
  };

  const handleProductPreview = async (productName) => {
    if (!selectedCustomer || !productName) {
      toast.error("Please select a product");
      return;
    }

    setDownloadingProductPdf(productName);
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(
        `${BASE_URL}${API_ENDPOINTS.PASSBOOK.DOWNLOAD_PRODUCT(selectedCustomer.id, productName)}`,
        {
          method: "GET",
          headers: {
            "Authorization": `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error("Failed to generate product PDF");
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      setPdfUrl(url);
      setSelectedProduct(productName);
      setPdfModalOpen(true);
    } catch (error) {
      console.error("Error generating product PDF:", error);
      toast.error("Failed to generate product PDF");
    } finally {
      setDownloadingProductPdf(null);
    }
  };

  // eslint-disable-next-line no-unused-vars
  const handleStatementDownload = () => {
    if (!pdfUrl) return;
    const a = document.createElement('a');
    a.href = pdfUrl;
    a.download = `${selectedCustomer.name}_statement.pdf`;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(pdfUrl);
    document.body.removeChild(a);
    toast.success("Statement PDF downloaded successfully");
  };

  const handleBackToMain = () => {
    setCurrentDialog('main');
    setStatementDialogOpen(false);
    setProductDialogOpen(false);
    setPassbookDialogOpen(true);
  };


  // === DELETE ===
  const handleDelete = async (id) => {
    // Open custom confirmation modal instead of window.confirm
    setConfirmModal({ isOpen: true, customerId: id });
  };

  const confirmDelete = async () => {
    try {
      const response = await fetch(`${BASE_URL}${API_ENDPOINTS.CUSTOMERS.DELETE_BY_ID(confirmModal.customerId)}`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });

      if (!response.ok) throw new Error();

      toast.success("Customer deleted successfully");
      fetchCustomers();
      setConfirmModal({ isOpen: false, customerId: null });

    } catch {
      toast.error("Delete failed");
      setConfirmModal({ isOpen: false, customerId: null });
    }
  };

  const cancelDelete = () => {
    setConfirmModal({ isOpen: false, customerId: null });
  };

  const customerHeaderCellSx = {
    fontWeight: 700,
    backgroundColor: "#FFFFFF",
    fontSize: 12,
    letterSpacing: 0.6,
    textTransform: "uppercase",
    color: "#475569",
    padding: {
      xs: "12px 10px",
      md: "16px",
    },
    whiteSpace: "nowrap",
  };


  return (
    <Box className="customer-details-container">

        {/* Custom Confirmation Modal */}
        <ConfirmModal
          isOpen={confirmModal.isOpen}
          onConfirm={confirmDelete}
          onCancel={cancelDelete}
          title="Delete Customer"
          message="Are you sure you want to delete this customer? This action cannot be undone and will remove all associated challans and passbook entries."
          confirmText="Delete Customer"
          cancelText="Cancel"
        />

      {/* Header Section */}
      <Box sx={{ gap: 3, mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
          {/* Left - Title */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: { xs: 1.5, sm: 2 },
              flex: 1,
              minWidth: 0,
              flexBasis: { xs: '100%', sm: 'auto' },
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: { xs: 40, sm: 48 },
                height: { xs: 40, sm: 48 },
                flexShrink: 0,
                background: '#4338ca',
                borderRadius: '12px',
                color: 'white',
              }}
            >
              <PersonIcon sx={{ fontSize: { xs: 20, sm: 24 } }} />
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography
                variant="h4"
                sx={{ fontSize: { xs: '1.35rem', sm: '1.7rem', md: '2rem' }, fontWeight: 700, color: 'text.primary', mb: 0.5, lineHeight: 1.2 }}
              >
                View Customers
              </Typography>
              <Typography variant="body1" sx={{ color: 'text.secondary', fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                Manage customer information and details
              </Typography>
            </Box>
          </Box>

          {/* Right - Actions */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: { xs: 'stretch', sm: 'flex-end' },
              gap: { xs: 1.5, sm: 2 },
              flexWrap: 'wrap',
              width: { xs: '100%', sm: 'auto' },
            }}
          >
          {/* Export / Import grouped as a single segmented control */}
          <Box
            sx={{
              display: "flex",
              width: { xs: "100%", sm: "auto" },
              flex: { xs: "1 1 100%", sm: "0 0 auto" },
              minWidth: 0,
              border: isDark ? "1px solid #334155" : "1px solid #E2E8F0",
              borderRadius: "12px",
              overflow: "hidden",
              backgroundColor: isDark ? "#1e293b" : "#F8FAFC",
              boxShadow: isDark ? "0 1px 3px rgba(0, 0, 0, 0.3)" : "0 1px 3px rgba(0, 0, 0, 0.05)",
            }}
          >
            <Button
              onClick={handleExport}
              startIcon={<UploadIcon fontSize="small" sx={{ color: "#2563EB" }} />}
              sx={{
                color: "#2563EB",
                fontWeight: 600,
                borderRadius: 0,
                flex: 1,
                minWidth: 0,
                px: { xs: 1.25, sm: 2 },
                py: 1,
                fontSize: { xs: "13px", sm: "14px" },
                whiteSpace: "nowrap",
                "&:hover": { backgroundColor: isDark ? "rgba(37, 99, 235, 0.15)" : "#EFF6FF" },
              }}
            >
              Export
            </Button>
            <Divider 
              orientation="vertical" 
              flexItem 
              sx={{ borderColor: isDark ? "#334155" : "#E2E8F0" }}
            />
            <Button
              onClick={handleImport}
              startIcon={<DownloadIcon fontSize="small" sx={{ color: "#059669" }} />}
              sx={{
                color: "#059669",
                fontWeight: 600,
                borderRadius: 0,
                flex: 1,
                minWidth: 0,
                px: { xs: 1.25, sm: 2 },
                py: 1,
                fontSize: { xs: "13px", sm: "14px" },
                whiteSpace: "nowrap",
                "&:hover": { backgroundColor: isDark ? "rgba(5, 150, 105, 0.15)" : "#ECFDF5" },
              }}
            >
              Import
            </Button>
          </Box>

          {/* Template grouped as a single segmented control */}
          <Box
            sx={{
              display: "flex",
              border: isDark ? "1px solid #334155" : "1px solid #E2E8F0",
              borderRadius: "12px",
              overflow: "hidden",
              backgroundColor: isDark ? "#1e293b" : "#F8FAFC",
              boxShadow: isDark ? "0 1px 3px rgba(0, 0, 0, 0.3)" : "0 1px 3px rgba(0, 0, 0, 0.05)",
              width: { xs: "100%", sm: "auto" },
            }}
          >
            <Button
              onClick={handleDownloadTemplate}
              startIcon={<DownloadIcon fontSize="small" sx={{ color: "#7C3AED" }} />}
              sx={{
                color: "#7C3AED",
                fontWeight: 600,
                borderRadius: 0,
                flex: 1,
                minWidth: 0,
                px: { xs: 1.25, sm: 2 },
                py: 1,
                fontSize: { xs: "13px", sm: "14px" },
                whiteSpace: "nowrap",
                "&:hover": { backgroundColor: isDark ? "rgba(124, 58, 237, 0.15)" : "#F5F3FF" },
              }}
            >
              Download Template
            </Button>
            {/* Temporarily disabled
            <Divider 
              orientation="vertical" 
              flexItem 
              sx={{ borderColor: isDark ? "#334155" : "#E2E8F0" }}
            />
            <Button
              onClick={handleViewTemplate}
              startIcon={<UploadIcon fontSize="small" sx={{ color: "#0891B2" }} />}
              disabled={loadingTemplate}
              sx={{
                color: "#0891B2",
                fontWeight: 600,
                borderRadius: 0,
                px: 2,
                py: 1,
                "&:hover": { backgroundColor: isDark ? "rgba(8, 145, 178, 0.15)" : "#ECFEFF" },
              }}
            >
              View Template
            </Button>
            */}
          </Box>
          <Button variant="outlined" startIcon={isRefreshing ? <AnimatedRefreshIcon size={18} /> : <RefreshIcon />} onClick={handleRefresh} disabled={loading} sx={{
            flex: { xs: 1, sm: "0 0 auto" },
            height: { xs: 42, sm: 46 },
            minWidth: { xs: 0, sm: 135 },
            borderRadius: "12px",
            fontSize: { xs: "13px", sm: "14px" },
            fontWeight: 600,
            whiteSpace: "nowrap",
          }}>
            Refresh
          </Button>
          <Button
            variant="contained"
            onClick={() => navigate("/dashboard/customers/create")}
            sx={{
              flex: { xs: 1, sm: "0 0 auto" },
              height: { xs: 42, sm: 46 },
              minWidth: { xs: 0, sm: 135 },
              borderRadius: "12px",
              fontSize: { xs: "13px", sm: "14px" },
              fontWeight: 600,
              whiteSpace: "nowrap",
            }}
          >
            Create Customer
          </Button>
        </Box>
      </Box>

      {/* Filter Section */}
      <Box
        sx={{
          mt: 2,
          mb: 3,
          position: "relative",
          zIndex: 10,
        }}
      >
        {!filterOpen ? (
          <Button
            variant="outlined"
            startIcon={<SearchIcon />}
            onClick={() => setFilterOpen(true)}
            sx={{
              width: { xs: "100%", sm: "auto" },
              height: { xs: 44, sm: 48 },
              px: 2,
              borderRadius: "10px",
              fontWeight: 600,
              textTransform: "none",
              fontSize: { xs: "13px", sm: "14px" },
              justifyContent: "center",
              borderColor: "#E5E7EB",
              color: "#64748B",
              backgroundColor: "transparent",
              "&:hover": {
                borderColor: "#2563EB",
                backgroundColor: "#EFF6FF",
                color: "#2563EB",
              },
            }}
          >
            Show Filters
          </Button>
        ) : (
        <Card
          sx={{
            mb: 3,
            borderRadius: "18px",
            border: "1px solid #E5E7EB",
            boxShadow: "0 8px 24px rgba(15, 23, 42, 0.06)",
            overflow: "hidden",
            background: "#FFFFFF",
            position: "relative",
            zIndex: 20,
          }}
        >
          <CardContent
            sx={{
              p: { xs: 2, sm: 2.5 }
            }}
          >
            <Box sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              mb: 2,
            }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Box sx={{
                  width: 28,
                  height: 28,
                  borderRadius: "8px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  background: "#eff6ff",
                  color: "#2563eb",
                }}>
                  <SearchIcon sx={{ fontSize: 16 }} />
                </Box>
                <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#0f172a" }}>
                  Search Filters
                </Typography>
              </Box>

              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Button
                  variant="text"
                  size="small"
                  startIcon={<ClearIcon sx={{ fontSize: 14 }} />}
                  onClick={clearAllFilters}
                  disabled={!searchTerm}
                  sx={{
                    height: 32,
                    borderRadius: "8px",
                    fontWeight: 600,
                    fontSize: "12px",
                  }}
                >
                  Clear
                </Button>
                <Button
                  variant="text"
                  size="small"
                  onClick={() => setFilterOpen(false)}
                  sx={{
                    height: 32,
                    borderRadius: "8px",
                    fontWeight: 600,
                    fontSize: "12px",
                    color: "#64748b",
                    "&:hover": {
                      background: "#f1f5f9",
                      color: "#0f172a",
                    }
                  }}
                >
                  Close
                </Button>
              </Box>
            </Box>

            <Box sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr" },
              gap: 2
            }}>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                <Typography sx={{ fontSize: 11, fontWeight: 700, letterSpacing: 0.5, color: "#64748B" }}>
                  Search by Name or Email
                </Typography>
                <input
                  type="text"
                  placeholder="Search customers..."
                  value={searchTerm}
                  onChange={handleSearch}
                  style={{
                    width: "100%",
                    height: 46,
                    padding: "0 14px",
                    border: "1px solid #E2E8F0",
                    borderRadius: "12px",
                    outline: "none",
                    fontSize: "14px",
                    color: "#0F172A",
                    backgroundColor: "#F8FAFC",
                    boxSizing: "border-box",
                    cursor: "text",
                    WebkitAppearance: "none",
                    MozAppearance: "none",
                    appearance: "none"
                  }}
                />
              </Box>
            </Box>
          </CardContent>
        </Card>
        )}
      </Box>

      {/* Customer Table Card */}
      <Box
        sx={{
          position: "relative",
          zIndex: 1,
          width: "100%",
          backgroundColor: "#FFFFFF",
          border: "1px solid #E5E7EB",
          borderRadius: "18px",
          boxShadow: "0 6px 20px rgba(15, 23, 42, 0.05)",
          overflow: "hidden",
        }}
      >
        <TableContainer
          sx={{
            width: "100%",
            maxWidth: "100%",
            overflowX: 'auto',
            overflowY: 'auto',
            "&::-webkit-scrollbar": {
              width: 8,
              height: 8,
            },
            "&::-webkit-scrollbar-thumb": {
              borderRadius: 20,
              background: "#cbd5e1",
            },
          }}
        >
          <Table
            sx={{
              minWidth: 920,
              width: "100%",
            }}
          >
            <TableHead sx={{
              "& th": {
                position: "sticky",
                top: 0,
                zIndex: 5,
                backgroundColor: "#FFFFFF",
              },
            }}
          >
            <TableRow>
              <TableCell sx={customerHeaderCellSx}>
                Name
              </TableCell>
              <TableCell
                align="center"
                sx={customerHeaderCellSx}
              >
                Mobile
              </TableCell>
              <TableCell
                align="center"
                sx={customerHeaderCellSx}
              >
                Email
              </TableCell>
              <TableCell
                align="center"
                sx={customerHeaderCellSx}
              >
                Address
              </TableCell>
              <TableCell
                align="center"
                sx={customerHeaderCellSx}
              >
                Passbook
              </TableCell>
              <TableCell
                align="center"
                sx={customerHeaderCellSx}
              >
                <Box sx={{ width: 120, maxWidth: '100%', mx: 'auto', textAlign: 'center' }}>
                  Actions
                </Box>
              </TableCell>
            </TableRow>
          </TableHead>
            <TableBody>
              {loading ? (
                [...Array(rowsPerPage)].map((_, index) => (
                  <TableRow key={index} sx={{ height: 54 }}>
                    <TableCell align="center"><Skeleton variant="text" width={120} /></TableCell>
                    <TableCell align="center"><Skeleton variant="text" width={100} /></TableCell>
                    <TableCell align="center"><Skeleton variant="text" width={150} /></TableCell>
                    <TableCell align="center"><Skeleton variant="text" width={180} /></TableCell>
                    <TableCell align="center"><Skeleton variant="text" width={80} /></TableCell>
                    <TableCell align="center">
                      <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                        <Skeleton variant="circular" width={32} height={32} />
                        <Skeleton variant="circular" width={32} height={32} />
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              ) : customers.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 8 }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                      <Box sx={{
                        width: 64,
                        height: 64,
                        borderRadius: '16px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        background: '#EEF2FF',
                        opacity: 0.7
                      }}>
                        <PersonIcon sx={{ fontSize: 36, color: '#4F46E5' }} />
                      </Box>
                      <Typography variant="h6" color="text.primary" fontWeight={600}>
                        No Customers Found
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Try changing your search or create a new customer
                      </Typography>
                      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 1.5, mt: 1, width: { xs: '100%', sm: 'auto' }, maxWidth: 320 }}>
                        <Button
                          variant="outlined"
                          onClick={clearAllFilters}
                          sx={{ width: { xs: '100%', sm: 'auto' } }}
                        >
                          Clear Search
                        </Button>
                        <Button
                          variant="contained"
                          onClick={() => navigate("/dashboard/customers/create")}
                          sx={{ width: { xs: '100%', sm: 'auto' } }}
                        >
                          Create Customer
                        </Button>
                      </Box>
                    </Box>
                  </TableCell>
                </TableRow>
              ) : (
                customers.map((customer) => (
                  <TableRow
                    key={customer.id}
                    hover
                    sx={{
                      height: 54,
                      "&:hover": {
                        backgroundColor: "#F8FAFC",
                      },
                      "& td": {
                        padding: {
                          xs: "10px",
                          md: "12px 14px",
                        },
                        verticalAlign: "middle",
                      },
                      transition: "background-color 0.15s ease",
                    }}
                  >
                    <TableCell sx={{ textAlign: 'left' }}>
                      <Typography 
                        variant="body2" 
                        fontWeight={600} 
                        sx={{ 
                          textAlign: 'left',
                          cursor: 'pointer',
                          color: '#2563eb',
                          '&:hover': {
                            textDecoration: 'underline',
                            color: '#1d4ed8'
                          }
                        }}
                        onClick={() => viewPassbook(customer.id, customer.name)}
                      >
                        {customer.name}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Typography variant="body2" sx={{ textAlign: 'center' }}>
                        {customer.contactNumber}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Typography variant="body2" sx={{ textAlign: 'center' }}>
                        {customer.email || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title={customer.address || '-'} arrow>
                        <Typography variant="body2" sx={{ textAlign: 'center', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {customer.address || '-'}
                        </Typography>
                      </Tooltip>
                    </TableCell>
                    <TableCell align="center">
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<BookIcon sx={{ fontSize: 14 }} />}
                        onClick={(e) => handlePassbookMenuClick(e, customer)}
                        sx={{
                          minWidth: 142,
                          height: 38,
                          px: 1.5,
                          borderRadius: "9px",
                          borderColor: "#4F46E5",
                          color: "#4338CA",
                          fontSize: "12px",
                          fontWeight: 600,
                          textTransform: "none",
                          whiteSpace: "nowrap",
                          "&:hover": {
                            borderColor: "#4338CA",
                            backgroundColor: "#EEF2FF",
                          },
                          "& .MuiButton-startIcon": {
                            marginRight: "7px",
                          },
                        }}
                      >
                        View Passbook
                      </Button>
                    </TableCell>
                    <TableCell align="center">
                      <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                        <IconButton
                          size="small"
                          onClick={() => navigate(`/dashboard/customers/${customer.id}/edit`)}
                          sx={{
                            width: 32,
                            height: 32,
                            borderRadius: '8px',
                            background: '#fef3c7',
                            color: '#d97706',
                            '&:hover': {
                              background: '#fde68a',
                            }
                          }}
                        >
                          <EditIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleDelete(customer.id)}
                          sx={{
                            width: 32,
                            height: 32,
                            borderRadius: '8px',
                            background: '#fee2e2',
                            color: '#dc2626',
                            '&:hover': {
                              background: '#fecaca',
                            }
                          }}
                        >
                          <DeleteIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Pagination Footer */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 1.5,
            px: { xs: 2, md: 2.5 },
            py: 1.5,
            borderTop: isDark ? "1px solid #334155" : "1px solid #E5E7EB",
            backgroundColor: isDark ? "#0f172a" : "#FFFFFF",
          }}
        >
          {/* Top row: Showing records */}
          {customers.length > 0 && (
            <Typography
              variant="caption"
              sx={{
                color: isDark ? "#94A3B8" : "#64748B",
                fontSize: "13px",
                fontWeight: 500,
              }}
            >
              Showing{" "}
              {currentPage * rowsPerPage + 1}
              –{Math.min((currentPage + 1) * rowsPerPage, customers.length)} of{" "}
              {customers.length} records
            </Typography>
          )}

          {/* Bottom row: Pagination and Rows per page */}
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              justifyContent: "space-between",
              alignItems: "center",
              flexWrap: "wrap",
              gap: { xs: 1, sm: 2 },
              width: "100%",
            }}
          >
            {/* Left: Pagination */}
            <Pagination
              count={totalPages}
              page={currentPage + 1}
              onChange={(event, value) => setCurrentPage(value - 1)}
              disabled={loading}
              shape="rounded"
              variant="outlined"
              color="primary"
              showFirstButton={!isMobile}
              showLastButton={!isMobile}
              siblingCount={0}
              boundaryCount={1}
              size="small"
              sx={{
                maxWidth: "100%",
                "& ul": {
                  flexWrap: "wrap",
                  justifyContent: "center",
                  gap: { xs: "4px", sm: 0 },
                },
                "& .MuiPaginationItem-root": {
                  transition: "transform .2s, box-shadow .2s, background-color .2s",
                  borderRadius: { xs: "8px", sm: "10px" },
                  fontWeight: 600,
                  minWidth: { xs: 32, sm: 38 },
                  height: { xs: 32, sm: 38 },
                  margin: { xs: 0, sm: "0 3px" },
                  fontSize: { xs: "0.78rem", sm: "0.875rem" },
                  borderColor: isDark ? "#475569" : undefined,
                  color: isDark ? "#E2E8F0" : undefined,
                },
                "& .MuiPaginationItem-root:hover": {
                  transform: "translateY(-2px)",
                  boxShadow: isDark ? "0 4px 10px rgba(0,0,0,.4)" : "0 4px 10px rgba(0,0,0,.15)",
                  backgroundColor: isDark ? "#334155" : undefined,
                },
                "& .MuiPaginationItem-root:active": {
                  transform: "scale(.95)",
                },
                "& .Mui-selected": {
                  transform: "scale(1.05)",
                  boxShadow: isDark ? "0 6px 14px rgba(37,99,235,.4)" : "0 6px 14px rgba(37,99,235,.25)",
                },
                "& .Mui-disabled": {
                  color: isDark ? "#475569" : undefined,
                  borderColor: isDark ? "#334155" : undefined,
                },
              }}
            />

            {/* Right: Rows per page */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: { xs: 1, sm: 1.25 },
                width: "auto",
                marginLeft: "auto",
                marginRight: 0,
              }}
            >
              <Typography sx={{ fontSize: { xs: "0.8rem", sm: "0.875rem" }, fontWeight: 500, color: isDark ? "#94A3B8" : "#475569", whiteSpace: "nowrap" }}>Rows per page</Typography>
              <Box className="rows-per-page-select" sx={{ width: { xs: 72, sm: 80 } }}>
                <AppDropdown
                  value={rowsPerPage}
                  options={[
                    { value: 5, label: '5' },
                    { value: 10, label: '10' },
                    { value: 20, label: '20' },
                    { value: 50, label: '50' },
                    { value: 100, label: '100' }
                  ]}
                  onChange={(newSize) => {
                    setRowsPerPage(newSize);
                    setCurrentPage(0);
                  }}
                />
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Passbook Dialog */}
      <Dialog
        open={passbookDialogOpen}
        onClose={handlePassbookMenuClose}
        sx={{ zIndex: 1300 }}
        PaperProps={{
          sx: {
            width: isMobile ? '95vw' : 720,
            maxWidth: isMobile ? '95vw' : 720,
            maxHeight: isMobile ? '90vh' : '85vh',
            margin: isMobile ? '16px' : 'auto',
            borderRadius: 18,
            overflow: 'hidden',
          }
        }}
      >
        <DialogTitle sx={{ pb: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700, color: '#1e293b', fontSize: '18px', display: 'flex', alignItems: 'center', gap: 1 }}>
                <BookIcon sx={{ fontSize: 24, color: '#4338ca' }} />
                Passbook
              </Typography>
              {selectedCustomer && (
                <Typography variant="body2" sx={{ color: '#64748b', fontSize: '14px', mt: 0.5 }}>
                  {selectedCustomer.name}
                </Typography>
              )}
            </Box>
            <IconButton onClick={handlePassbookMenuClose} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Button
              fullWidth
              onClick={handleViewPassbookClick}
              sx={{
                height: 48,
                borderRadius: 12,
                textTransform: 'none',
                fontWeight: 600,
                fontSize: '14px',
                justifyContent: 'flex-start',
                px: 2,
                color: '#1e293b',
                background: '#F8FAFC',
                '&:hover': {
                  background: '#F1F5F9',
                }
              }}
            >
              <BookIcon sx={{ fontSize: 20, mr: 2, color: '#4338ca' }} />
              View Passbook
            </Button>
            <Button
              fullWidth
              onClick={handleDownloadStatementClick}
              sx={{
                height: 48,
                borderRadius: 12,
                textTransform: 'none',
                fontWeight: 600,
                fontSize: '14px',
                justifyContent: 'flex-start',
                px: 2,
                color: '#1e293b',
                background: '#F8FAFC',
                '&:hover': {
                  background: '#F1F5F9',
                }
              }}
            >
              <DescriptionIcon sx={{ fontSize: 20, mr: 2, color: '#059669' }} />
              Statement PDF
            </Button>
            <Button
              fullWidth
              onClick={handleDownloadProductClick}
              sx={{
                height: 48,
                borderRadius: 12,
                textTransform: 'none',
                fontWeight: 600,
                fontSize: '14px',
                justifyContent: 'flex-start',
                px: 2,
                color: '#1e293b',
                background: '#F8FAFC',
                '&:hover': {
                  background: '#F1F5F9',
                }
              }}
            >
              <InventoryIcon sx={{ fontSize: 20, mr: 2, color: '#d97706' }} />
              Product PDF
            </Button>
          </Box>
        </DialogContent>
        <Divider />
        <DialogActions sx={{ px: 3, py: 2, justifyContent: 'flex-end', gap: 2 }}>
          <Button
            onClick={handlePassbookMenuClose}
            sx={{
              height: 46,
              minWidth: 110,
              borderRadius: 12,
              textTransform: 'none',
              fontWeight: 600,
              background: '#F8FAFC',
              border: '1px solid #CBD5E1',
            }}
          >
            Cancel
          </Button>
        </DialogActions>
      </Dialog>

      {/* Statement Date Filter Dialog */}
      <Dialog
        open={statementDialogOpen}
        onClose={() => setStatementDialogOpen(false)}
        sx={{ zIndex: 1300 }}
        PaperProps={{
          sx: {
            width: isMobile ? '95vw' : 720,
            maxWidth: isMobile ? '95vw' : 720,
            maxHeight: isMobile ? '90vh' : '85vh',
            margin: isMobile ? '16px' : 'auto',
            borderRadius: 18,
            overflow: 'hidden',
          }
        }}
      >
        <DialogTitle sx={{ pb: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700, color: '#1e293b', fontSize: '18px', display: 'flex', alignItems: 'center', gap: 1 }}>
                <DescriptionIcon sx={{ fontSize: 24, color: '#059669' }} />
                Statement PDF
              </Typography>
              {selectedCustomer && (
                <Typography variant="body2" sx={{ color: '#64748b', fontSize: '14px', mt: 0.5 }}>
                  {selectedCustomer.name}
                </Typography>
              )}
            </Box>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                onClick={handleBackToMain}
                startIcon={<ArrowBackIcon sx={{ fontSize: 18 }} />}
                sx={{
                  height: 40,
                  borderRadius: 8,
                  textTransform: 'none',
                  fontWeight: 600,
                  fontSize: '13px',
                }}
              >
                Back
              </Button>
              <IconButton onClick={() => setStatementDialogOpen(false)} size="small">
                <CloseIcon />
              </IconButton>
            </Box>
          </Box>
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: 3 }}>
            <TextField
              label="From Date"
              type="date"
              fullWidth
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              InputLabelProps={{
                shrink: true,
              }}
              sx={{
                '& .MuiInputBase-root': {
                  height: 56,
                  borderRadius: 12,
                },
                '& .MuiInputLabel-root': {
                  transform: 'translate(14px, -9px) scale(0.75)',
                },
              }}
            />
            <TextField
              label="To Date"
              type="date"
              fullWidth
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              InputLabelProps={{
                shrink: true,
              }}
              sx={{
                '& .MuiInputBase-root': {
                  height: 56,
                  borderRadius: 12,
                },
                '& .MuiInputLabel-root': {
                  transform: 'translate(14px, -9px) scale(0.75)',
                },
              }}
            />
          </Box>
        </DialogContent>
        <Divider />
        <DialogActions sx={{ px: 3, py: 2, justifyContent: 'flex-end', gap: 2 }}>
          <Button
            onClick={() => setStatementDialogOpen(false)}
            sx={{
              height: 46,
              minWidth: 110,
              borderRadius: 12,
              textTransform: 'none',
              fontWeight: 600,
              background: '#F8FAFC',
              border: '1px solid #CBD5E1',
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleStatementPreview}
            disabled={downloadingPdf}
            variant="contained"
            sx={{
              height: 46,
              minWidth: 140,
              borderRadius: 12,
              textTransform: 'none',
              fontWeight: 600,
            }}
          >
            {downloadingPdf ? <CircularProgress size={20} sx={{ mr: 1 }} /> : <DescriptionIcon sx={{ mr: 1, fontSize: 18 }} />}
            {downloadingPdf ? 'Generating...' : 'Preview PDF'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Product Selection Dialog */}
      <Dialog
        open={productDialogOpen}
        onClose={() => setProductDialogOpen(false)}
        sx={{ zIndex: 1300 }}
        PaperProps={{
          sx: {
            width: isMobile ? '95vw' : 720,
            maxWidth: isMobile ? '95vw' : 720,
            maxHeight: isMobile ? '90vh' : '85vh',
            margin: isMobile ? '16px' : 'auto',
            borderRadius: 18,
            overflow: 'hidden',
          }
        }}
      >
        <DialogTitle sx={{ pb: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700, color: '#1e293b', fontSize: '18px', display: 'flex', alignItems: 'center', gap: 1 }}>
                <InventoryIcon sx={{ fontSize: 24, color: '#d97706' }} />
                Product PDF
              </Typography>
              <Typography variant="body2" sx={{ color: '#64748b', fontSize: '14px', mt: 0.5 }}>
                Choose Product
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                onClick={handleBackToMain}
                startIcon={<ArrowBackIcon sx={{ fontSize: 18 }} />}
                sx={{
                  height: 40,
                  borderRadius: 8,
                  textTransform: 'none',
                  fontWeight: 600,
                  fontSize: '13px',
                }}
              >
                Back
              </Button>
              <IconButton onClick={() => setProductDialogOpen(false)} size="small">
                <CloseIcon />
              </IconButton>
            </Box>
          </Box>
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ p: 3 }}>
          {loadingProducts ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {products.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 8, color: '#64748b', fontSize: '14px' }}>
                  No products found
                </Box>
              ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {products.map((product, index) => {
                    const productName = product.itemName || product.name || product;
                    const isSelected = selectedProduct === productName;
                    return (
                      <Box
                        key={index}
                        onClick={() => handleProductPreview(productName)}
                        disabled={downloadingProductPdf === productName}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 2,
                          p: 3,
                          borderRadius: 12,
                          border: isSelected ? '2px solid #4338ca' : '1px solid #E5E7EB',
                          background: isSelected ? '#EEF2FF' : '#F8FAFC',
                          cursor: 'pointer',
                          transition: 'all 0.2s ease',
                          '&:hover': {
                            background: '#E0E7FF',
                            borderColor: '#4338ca',
                          },
                          '&:disabled': {
                            opacity: 0.6,
                            cursor: 'not-allowed',
                          },
                        }}
                      >
                        <InventoryIcon sx={{ fontSize: 24, color: '#d97706' }} />
                        <Typography sx={{ fontSize: '15px', fontWeight: 600, color: '#1e293b', flex: 1 }}>
                          {productName}
                        </Typography>
                        {downloadingProductPdf === productName && (
                          <CircularProgress size={20} />
                        )}
                      </Box>
                    );
                  })}
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <Divider />
        <DialogActions sx={{ px: 3, py: 2, justifyContent: 'flex-end', gap: 2 }}>
          <Button
            onClick={() => setProductDialogOpen(false)}
            sx={{
              height: 46,
              minWidth: 110,
              borderRadius: 12,
              textTransform: 'none',
              fontWeight: 600,
              background: '#F8FAFC',
              border: '1px solid #CBD5E1',
            }}
          >
            Cancel
          </Button>
        </DialogActions>
      </Dialog>

      </Box>

      {/* PDF Preview Modal */}
      <PdfPreviewModal
        open={pdfModalOpen}
        onClose={() => {
          setPdfModalOpen(false);
          setPdfUrl(null);
        }}
        pdfUrl={pdfUrl}
        fileName={pdfType === 'statement' ? `${selectedCustomer?.name}_statement.pdf` : `${selectedCustomer?.name}_${selectedProduct}.pdf`}
      />

      <PassbookDownloadModal
        isOpen={passbookModal.isOpen}
        onClose={() => setPassbookModal({ isOpen: false, customerId: null, customerName: null })}
        customerId={passbookModal.customerId}
        customerName={passbookModal.customerName}
      />

      {/* Template Preview Modal */}
      <TemplatePreviewModal
        open={templateModalOpen}
        onClose={() => setTemplateModalOpen(false)}
        loading={loadingTemplate}
        templateHeaders={templateHeaders}
        onDownload={handleDownloadTemplate}
        onImport={handleImport}
        isDark={isDark}
      />

      {/* Popup Message Dialog */}
      <Dialog
        open={popupMessage.open}
        onClose={() => setPopupMessage({ ...popupMessage, open: false })}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "16px",
            boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
          }
        }}
      >
        <DialogTitle sx={{
          fontSize: "1.25rem",
          fontWeight: 700,
          color: popupMessage.type === 'error' ? '#EF4444' : popupMessage.type === 'warning' ? '#F59E0B' : '#10B981',
          pb: 1
        }}>
          {popupMessage.title}
        </DialogTitle>
        <DialogContent sx={{ pb: 2 }}>
          <Typography variant="body1" sx={{ color: '#64748B', lineHeight: 1.6 }}>
            {popupMessage.message}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 1, gap: 1 }}>
          {popupMessage.errorReport && (
            <Button
              onClick={handleDownloadErrorReport}
              variant="outlined"
              startIcon={<DownloadIcon />}
              sx={{
                borderRadius: "10px",
                fontWeight: 600,
                textTransform: "none",
                borderColor: '#E2E8F0',
                color: '#64748B',
                '&:hover': {
                  borderColor: '#CBD5E1',
                  backgroundColor: '#F8FAFC',
                }
              }}
            >
              Download Report
            </Button>
          )}
          <Button
            onClick={() => setPopupMessage({ ...popupMessage, open: false })}
            variant="contained"
            sx={{
              borderRadius: "10px",
              fontWeight: 600,
              textTransform: "none",
              backgroundColor: popupMessage.type === 'error' ? '#EF4444' : popupMessage.type === 'warning' ? '#F59E0B' : '#10B981',
              '&:hover': {
                backgroundColor: popupMessage.type === 'error' ? '#DC2626' : popupMessage.type === 'warning' ? '#D97706' : '#059669',
              }
            }}
          >
            OK
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default CustomerDetailsPage;